// Version V1 corrigée pour le système unifié demi-journée
import { FC, Fragment, HTMLAttributes, useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { SubmitHandler, useForm } from "react-hook-form";
import Input from "shared/Input/Input";
import Select from "shared/Select/Select";
import { z } from "zod";
import FormItem from "./FormItem";
import Multiselect from "multiselect-react-dropdown";
import { Switch } from "@mui/material";
import { ErrorMessage } from "../ErrorMessages";
import { LocationFormProps } from "../interfaces";
import { DAYS_OF_WEEK, formSchema } from "../utlis";
import toast from "react-hot-toast";

interface PricingCategory {
  id: string;
  title: string;
  description: string;
  emoji: string;
  colorClass: string;
  enabled: boolean;
  isConfigOpen: boolean;
}

const LocationAvailibility: FC<LocationFormProps & HTMLAttributes<HTMLDivElement>> = ({
  isLoading,
  handleForm,
  data,
  FormData,
}) => {
  const eventsList = ["Concert", "Conférence", "Mariage", "Anniversaire", "Séminaire"];

  const [refreshKey, setRefreshKey] = useState(0); // Pour forcer le re-render
  const [halfdayOptions, setHalfdayOptions] = useState({
    morning: true,
    afternoon: false,
  });

  // Fonction pour répliquer les valeurs matin sur tous les jours
  const applyToAllMorning = (sourceDay: string) => {
    const sourceMorningRow = document.querySelector(`[data-halfday-type="morning"][data-day="${sourceDay}"]`);
    if (!sourceMorningRow) return;

    // Récupérer les valeurs de la ligne source
    const startHour =
      (sourceMorningRow.querySelector('[data-field="start"]') as HTMLSelectElement)?.value || "08:00";
    const endHour = (sourceMorningRow.querySelector('[data-field="end"]') as HTMLSelectElement)?.value || "14:00";
    const price = (sourceMorningRow.querySelector('[data-field="price"]') as HTMLInputElement)?.value || "";
    const capacity = (sourceMorningRow.querySelector('[data-field="capacity"]') as HTMLInputElement)?.value || "";
    const isEnabled =
      (sourceMorningRow.querySelector('[data-field="enabled"]') as HTMLInputElement)?.checked || false;

    // Appliquer aux autres jours (matin uniquement)
    DAYS_OF_WEEK.filter((day) => day.value !== sourceDay).forEach((day) => {
      const targetRow = document.querySelector(`[data-halfday-type="morning"][data-day="${day.value}"]`);
      if (targetRow && halfdayOptions.morning) {
        const startSelect = targetRow.querySelector('[data-field="start"]') as HTMLSelectElement;
        const endSelect = targetRow.querySelector('[data-field="end"]') as HTMLSelectElement;
        const priceInput = targetRow.querySelector('[data-field="price"]') as HTMLInputElement;
        const capacityInput = targetRow.querySelector('[data-field="capacity"]') as HTMLInputElement;
        const enabledSwitch = targetRow.querySelector('[data-field="enabled"]') as HTMLInputElement;

        if (startSelect) startSelect.value = startHour;
        if (endSelect) endSelect.value = endHour;
        if (priceInput) priceInput.value = price;
        if (capacityInput) capacityInput.value = capacity;
        if (enabledSwitch) enabledSwitch.checked = isEnabled;
      }
    });

    toast.success("Paramètres matin appliqués à tous les jours !");
  };

  // Fonction pour répliquer les valeurs après-midi sur tous les jours
  const applyToAllAfternoon = (sourceDay: string) => {
    const sourceAfternoonRow = document.querySelector(`[data-halfday-type="afternoon"][data-day="${sourceDay}"]`);
    if (!sourceAfternoonRow) return;

    // Récupérer les valeurs de la ligne source
    const startHour =
      (sourceAfternoonRow.querySelector('[data-field="start"]') as HTMLSelectElement)?.value || "14:00";
    const endHour =
      (sourceAfternoonRow.querySelector('[data-field="end"]') as HTMLSelectElement)?.value || "20:00";
    const price = (sourceAfternoonRow.querySelector('[data-field="price"]') as HTMLInputElement)?.value || "";
    const capacity =
      (sourceAfternoonRow.querySelector('[data-field="capacity"]') as HTMLInputElement)?.value || "";
    const isEnabled =
      (sourceAfternoonRow.querySelector('[data-field="enabled"]') as HTMLInputElement)?.checked || false;

    // Appliquer aux autres jours (après-midi uniquement)
    DAYS_OF_WEEK.filter((day) => day.value !== sourceDay).forEach((day) => {
      const targetRow = document.querySelector(`[data-halfday-type="afternoon"][data-day="${day.value}"]`);
      if (targetRow && halfdayOptions.afternoon) {
        const startSelect = targetRow.querySelector('[data-field="start"]') as HTMLSelectElement;
        const endSelect = targetRow.querySelector('[data-field="end"]') as HTMLSelectElement;
        const priceInput = targetRow.querySelector('[data-field="price"]') as HTMLInputElement;
        const capacityInput = targetRow.querySelector('[data-field="capacity"]') as HTMLInputElement;
        const enabledSwitch = targetRow.querySelector('[data-field="enabled"]') as HTMLInputElement;

        if (startSelect) startSelect.value = startHour;
        if (endSelect) endSelect.value = endHour;
        if (priceInput) priceInput.value = price;
        if (capacityInput) capacityInput.value = capacity;
        if (enabledSwitch) enabledSwitch.checked = isEnabled;
      }
    });

    toast.success("Paramètres après-midi appliqués à tous les jours !");
  };
  const [pricingCategories, setPricingCategories] = useState<PricingCategory[]>([
    {
      id: "hourly",
      title: "Tarifs à l'heure",
      description: `Minimum ${data?.nbreHour || 2} heures`,
      emoji: "🕐",
      colorClass: "blue",
      enabled: true, // Activé par défaut
      isConfigOpen: false,
    },
    {
      id: "evening",
      title: "Soirée événementiel",
      description: "Événements de soirée",
      emoji: "🌙",
      colorClass: "purple",
      enabled: false,
      isConfigOpen: false,
    },
    {
      id: "halfday",
      title: "Demi-journée",
      description: "Matin et/ou après-midi",
      emoji: "🌗",
      colorClass: "green",
      enabled: false,
      isConfigOpen: false,
    },
    {
      id: "fullday",
      title: "Journée complète",
      description: "Toute la journée",
      emoji: "☀️",
      colorClass: "orange",
      enabled: false,
      isConfigOpen: false,
    },
  ]);

  const getColorClasses = (colorClass: string) => {
    const colorMap = {
      blue: {
        bgIcon: "bg-blue-100 dark:bg-blue-900/30",
        bgButton: "bg-blue-50 dark:bg-blue-900/30",
        textButton: "text-blue-700 dark:text-blue-300",
        hoverButton: "hover:bg-blue-100 dark:hover:bg-blue-900/50",
      },
      purple: {
        bgIcon: "bg-purple-100 dark:bg-purple-900/30",
        bgButton: "bg-purple-50 dark:bg-purple-900/30",
        textButton: "text-purple-700 dark:text-purple-300",
        hoverButton: "hover:bg-purple-100 dark:hover:bg-purple-900/50",
      },
      green: {
        bgIcon: "bg-green-100 dark:bg-green-900/30",
        bgButton: "bg-green-50 dark:bg-green-900/30",
        textButton: "text-green-700 dark:text-green-300",
        hoverButton: "hover:bg-green-100 dark:hover:bg-green-900/50",
      },
      orange: {
        bgIcon: "bg-orange-100 dark:bg-orange-900/30",
        bgButton: "bg-orange-50 dark:bg-orange-900/30",
        textButton: "text-orange-700 dark:text-orange-300",
        hoverButton: "hover:bg-orange-100 dark:hover:bg-orange-900/50",
      },
    };
    return colorMap[colorClass as keyof typeof colorMap];
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
    setValue,
    trigger,
  } = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      reservation_type: data?.reservation_type ?? undefined,
      prices: [],
      cleaning_costs: data?.cleaning_costs ?? 0,
      cleaning_costs_events: data?.cleaning_costs_events ?? [],
      guarantee: data?.guarantee ?? 0,
    },
    mode: "onSubmit",
  });

  useEffect(() => {
    if (data) {
      // Only set reservation_type if it exists and is not empty
      if (data.reservation_type && data.reservation_type.trim() !== "") {
        setValue("reservation_type", data.reservation_type);
      }
      setValue("cleaning_costs", data.cleaning_costs ?? 0);
      setValue("cleaning_costs_events", data.cleaning_costs_events ?? []);
      setValue("guarantee", data.guarantee ?? 0);

      // Si des prix existent déjà, les charger
      if (data.prices && data.prices.length > 0) {
        setValue("prices", data.prices as any);
      } else {
        // Générer des prix par défaut pour la catégorie horaire
        const defaultPrices = DAYS_OF_WEEK.map((day) => ({
          price: 50,
          min: 1,
          max: (data as any)?.visitor || 50,
          startHour: "09:00",
          endHour: "18:00",
          selectedDay: day.label,
          type: "heure" as "heure" | "soirée" | "demi-journée" | "journée complète",
        }));
        setValue("prices", defaultPrices);
        console.log("🎯 Prix par défaut générés:", defaultPrices.length, "tarifs");
      }
    }
  }, [data]);

  useEffect(() => {
    if (handleForm.type === "chekLocationFormSix") {
      handleSubmit(onSubmit)();
    }
  }, [handleForm]);

  const onSubmit: SubmitHandler<z.infer<typeof formSchema>> = (data) => {
    // Validation stricte : au moins une catégorie doit être activée
    const hasEnabledCategories = pricingCategories.some((cat) => cat.enabled);
    if (!hasEnabledCategories) {
      console.log("❌ Aucune catégorie de tarification activée");
      toast.error("Vous devez activer au moins un type de tarification (horaire, soirée, etc.)");
      return; // Empêche la soumission
    }

    // Validation stricte : politique d'annulation obligatoire
    if (!data.reservation_type) {
      console.log("❌ Politique d'annulation manquante");
      toast.error("Vous devez sélectionner une politique d'annulation");
      return; // Empêche la soumission
    }

    // S'assurer que availableDays est un objet JSON
    const availableDays = {
      Lundi: true,
      Mardi: true,
      Mercredi: true,
      Jeudi: true,
      Vendredi: true,
      Samedi: true,
      Dimanche: true,
    };

    // Créer des prix basés sur les catégories activées
    let prices = data.prices || [];

    // Si pas de prix mais des catégories activées, générer les prix
    if (prices.length === 0) {
      pricingCategories.forEach((category) => {
        if (category.enabled) {
          if (category.id === "halfday") {
            // Pour halfday, générer selon les options activées
            DAYS_OF_WEEK.forEach((day) => {
              if (halfdayOptions.morning) {
                prices.push({
                  price: 0,
                  min: 1,
                  max: (data as any)?.visitor || 50,
                  startHour: "08:00",
                  endHour: "14:00",
                  selectedDay: day.label,
                  type: "demi-journée",
                });
              }
              if (halfdayOptions.afternoon) {
                prices.push({
                  price: 0,
                  min: 1,
                  max: (data as any)?.visitor || 50,
                  startHour: "14:00",
                  endHour: "20:00",
                  selectedDay: day.label,
                  type: "demi-journée",
                });
              }
            });
          } else {
            // Pour les autres catégories
            const typeMap = {
              hourly: "heure",
              evening: "soirée",
              fullday: "journée complète",
            } as const;

            const targetType = typeMap[category.id as keyof typeof typeMap];
            if (targetType) {
              DAYS_OF_WEEK.forEach((day) => {
                let defaultStartHour = "09:00";
                let defaultEndHour = "18:00";

                if (category.id === "evening") {
                  defaultStartHour = "18:00";
                  defaultEndHour = "02:00";
                }

                prices.push({
                  price: 0,
                  min: 1,
                  max: (data as any)?.visitor || 50,
                  startHour: defaultStartHour,
                  endHour: defaultEndHour,
                  selectedDay: day.label,
                  type: targetType as "heure" | "soirée" | "journée complète",
                });
              });
            }
          }
        }
      });
    }

    console.log("🎯 DONNEES TARIFICATION COLLECTÉES:");
    console.log("Prices générés:", prices);
    console.log("Prices length:", prices.length);
    console.log("Prices with valid price (>0):", prices.filter((p) => p.price > 0).length);
    console.log("Cleaning costs:", data.cleaning_costs);
    console.log("Guarantee:", data.guarantee);
    console.log("Reservation type:", data.reservation_type);
    console.log(
      "Enabled categories:",
      pricingCategories.filter((c) => c.enabled).map((c) => c.id)
    );

    const formDataToSend = {
      ...data,
      prices: prices,
      availableDays: availableDays,
    };

    console.log("📤 FORM DATA ENVOYÉES AU PARENT:", formDataToSend);

    FormData(formDataToSend);
  };

  const toggleCategoryEnabled = (categoryId: string) => {
    setPricingCategories((prev) => {
      const updated = prev.map((cat) =>
        cat.id === categoryId
          ? { ...cat, enabled: !cat.enabled, isConfigOpen: !cat.enabled ? false : cat.isConfigOpen }
          : cat
      );

      // Créer automatiquement les prix quand une catégorie est activée
      const enabledCategory = updated.find((cat) => cat.id === categoryId);
      if (enabledCategory?.enabled) {
        const currentPrices = getValues("prices") || [];
        const newPrices = [...currentPrices];

        // Pour halfday, créer les prix selon les toggles activés
        if (categoryId === "halfday") {
          DAYS_OF_WEEK.forEach((day) => {
            // Ajouter matin si activé
            if (halfdayOptions.morning) {
              const existsMorning = newPrices.some(
                (p) => p.selectedDay === day.label && p.type === "demi-journée" && p.startHour === "08:00"
              );
              if (!existsMorning) {
                newPrices.push({
                  price: 0,
                  min: 1,
                  max: (data as any)?.visitor || 50,
                  startHour: "08:00",
                  endHour: "14:00",
                  selectedDay: day.label,
                  type: "demi-journée",
                });
              }
            }

            // Ajouter après-midi si activé
            if (halfdayOptions.afternoon) {
              const existsAfternoon = newPrices.some(
                (p) => p.selectedDay === day.label && p.type === "demi-journée" && p.startHour === "14:00"
              );
              if (!existsAfternoon) {
                newPrices.push({
                  price: 0,
                  min: 1,
                  max: (data as any)?.visitor || 50,
                  startHour: "14:00",
                  endHour: "20:00",
                  selectedDay: day.label,
                  type: "demi-journée",
                });
              }
            }
          });
        } else {
          // Pour les autres catégories
          const typeMap = {
            hourly: "heure",
            evening: "soirée",
            fullday: "journée complète",
          } as const;

          DAYS_OF_WEEK.forEach((day) => {
            const targetType = typeMap[categoryId as keyof typeof typeMap];
            if (targetType) {
              const exists = newPrices.some((p) => p.selectedDay === day.label && p.type === targetType);

              if (!exists) {
                let defaultStartHour = "09:00";
                let defaultEndHour = "18:00";

                if (categoryId === "evening") {
                  defaultStartHour = "18:00";
                  defaultEndHour = "02:00";
                }

                newPrices.push({
                  price: 0,
                  min: 1,
                  max: (data as any)?.visitor || 50,
                  startHour: defaultStartHour,
                  endHour: defaultEndHour,
                  selectedDay: day.label,
                  type: targetType as "heure" | "soirée" | "journée complète",
                });
              }
            }
          });
        }

        setValue("prices", newPrices);
        console.log(`✅ Catégorie ${categoryId} activée - ${newPrices.length} prix générés`);
      } else {
        // Retirer les prix de cette catégorie quand elle est désactivée
        const currentPrices = getValues("prices") || [];
        let filteredPrices = [...currentPrices];

        if (categoryId === "halfday") {
          // Supprimer tous les prix de demi-journée
          filteredPrices = filteredPrices.filter((p) => p.type !== "demi-journée");
        } else {
          const typeMap = {
            hourly: "heure",
            evening: "soirée",
            fullday: "journée complète",
          } as const;

          const targetType = typeMap[categoryId as keyof typeof typeMap];
          if (targetType) {
            filteredPrices = filteredPrices.filter((p) => p.type !== targetType);
          }
        }

        setValue("prices", filteredPrices);
        console.log(`❌ Catégorie ${categoryId} désactivée - prix supprimés`);
      }

      return updated;
    });
  };

  const toggleCategoryConfig = (categoryId: string) => {
    setPricingCategories((prev) =>
      prev.map((cat) => (cat.id === categoryId ? { ...cat, isConfigOpen: !cat.isConfigOpen } : cat))
    );
  };

  // Fonction pour appliquer la configuration d'un jour à tous les autres jours
  const applyToAllDays = (sourceDay: string, categoryId: string) => {
    const currentPrices = getValues("prices") || [];

    const typeMap = {
      hourly: "heure",
      evening: "soirée",
      fullday: "journée complète",
    } as const;

    const targetType = typeMap[categoryId as keyof typeof typeMap] as "heure" | "soirée" | "journée complète";

    // Trouver la configuration source
    const sourcePrice = currentPrices.find((p) => p.selectedDay === sourceDay && p.type === targetType);

    if (!sourcePrice) {
      toast.error(`Aucune configuration trouvée pour ${sourceDay} (${targetType})`);
      return; // Pas de configuration source
    }

    // Appliquer à tous les autres jours
    const updatedPrices = [...currentPrices];

    DAYS_OF_WEEK.forEach((day) => {
      if (day.label !== sourceDay) {
        // Chercher si un prix existe déjà pour ce jour et ce type
        const existingIndex = updatedPrices.findIndex((p) => p.selectedDay === day.label && p.type === targetType);

        const newPrice = {
          ...sourcePrice,
          selectedDay: day.label,
        };

        if (existingIndex >= 0) {
          // Remplacer le prix existant
          updatedPrices[existingIndex] = newPrice;
        } else {
          // Ajouter un nouveau prix
          updatedPrices.push(newPrice);
        }
      }
    });

    // Mettre à jour le formulaire
    setValue("prices", updatedPrices);

    // Forcer le re-render des champs
    trigger("prices");
    setRefreshKey((prev) => prev + 1);

    toast.success(`Configuration de ${sourceDay} appliquée à tous les jours`);
  };

  return (
    <Fragment>
      <form className="w-full" onSubmit={handleSubmit(onSubmit)}>
        <div className="w-full">
          <div>
            <h2 className="text-2xl font-semibold">Prix et disponibilité de votre espace</h2>
          </div>

          <div className="my-5">
            <FormItem label="Politique d'annulation *">
              <div className="flex space-x-4">
                <div className="relative flex-1">
                  <input
                    className="hidden peer"
                    id="radio_1"
                    type="radio"
                    {...register("reservation_type")}
                    value="flexible"
                  />
                  <span className="box-content absolute block w-3 h-3 -translate-y-1/2 bg-white border-8 border-gray-300 rounded-full right-4 top-1/2 peer-checked:border-indigo-500"></span>
                  <label
                    className="flex flex-col p-4 border border-gray-300 rounded-lg cursor-pointer peer-checked:border-4 peer-checked:border-indigo-700"
                    htmlFor="radio_1"
                  >
                    <span className="mt-2 text-xl font-bold">Flexible</span>
                  </label>
                </div>
                <div className="relative flex-1">
                  <input
                    className="hidden peer"
                    id="radio_2"
                    type="radio"
                    {...register("reservation_type")}
                    value="stricte"
                  />
                  <span className="box-content absolute block w-3 h-3 -translate-y-1/2 bg-white border-8 border-gray-300 rounded-full right-4 top-1/2 peer-checked:border-indigo-500"></span>
                  <label
                    className="flex flex-col p-4 border border-gray-300 rounded-lg cursor-pointer peer-checked:border-4 peer-checked:border-indigo-700"
                    htmlFor="radio_2"
                  >
                    <span className="mt-2 text-xl font-bold">Stricte</span>
                  </label>
                </div>
              </div>
              <ErrorMessage message={errors.reservation_type?.message || ""} />
            </FormItem>

            <div className="mt-10">
              <div className="flex items-center justify-between mb-4">
                <label className="font-semibold">Disponibilités et tarifs</label>
              </div>
              <span className="block mt-2 text-sm text-neutral-500 dark:text-neutral-400">
                Configurez vos différents types de tarification
              </span>

              <div className="space-y-4 mt-6">
                {pricingCategories.map((category) => {
                  const colors = getColorClasses(category.colorClass);
                  return (
                    <div
                      key={category.id}
                      className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div
                            className={`w-12 h-12 ${colors.bgIcon} rounded-full flex items-center justify-center`}
                          >
                            <span className="text-xl">{category.emoji}</span>
                          </div>
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                              {category.title}
                            </h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400">{category.description}</p>
                          </div>
                        </div>

                        <div className="flex items-center space-x-4">
                          <div className="flex items-center space-x-2">
                            <span className="text-sm font-medium">Activer</span>
                            <Switch
                              checked={category.enabled}
                              onChange={() => toggleCategoryEnabled(category.id)}
                              disabled={isLoading}
                            />
                          </div>

                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              if (!category.enabled || isLoading) return;
                              toggleCategoryConfig(category.id);
                            }}
                            className={`${!category.enabled || isLoading ? "opacity-50 cursor-not-allowed pointer-events-none" : "text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"} text-sm font-medium transition-colors`}
                          >
                            {category.isConfigOpen ? "Fermer" : "Configurer"}
                          </a>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Configuration sections dynamiques */}
              {pricingCategories
                .filter((category) => category.isConfigOpen)
                .map((category) => (
                  <div
                    key={`config-${category.id}`}
                    className="mt-8 bg-gray-50 dark:bg-gray-900 rounded-xl p-6 border border-gray-200 dark:border-gray-700"
                  >
                    <div className="flex items-center justify-between mb-6">
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                        <span className="text-2xl mr-2">{category.emoji}</span>
                        Configuration - {category.title}
                      </h4>
                      <button
                        type="button"
                        onClick={() => toggleCategoryConfig(category.id)}
                        className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                      >
                        <span className="sr-only">Fermer</span>✕
                      </button>
                    </div>

                    {/* Section spéciale pour demi-journée */}
                    {category.id === "halfday" && (
                      <div className="mb-6 p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                        <h5 className="text-md font-medium text-gray-900 dark:text-white mb-4">
                          Choisir les créneaux disponibles
                        </h5>
                        <div className="flex space-x-6">
                          <div className="flex items-center space-x-3">
                            <Switch
                              checked={halfdayOptions.morning}
                              onChange={(e) =>
                                setHalfdayOptions((prev) => ({ ...prev, morning: e.target.checked }))
                              }
                              disabled={isLoading}
                            />
                            <div>
                              <div className="text-sm font-medium text-gray-900 dark:text-white">
                                🌅 Matin (8h - 14h)
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">6 heures de location</div>
                            </div>
                          </div>
                          <div className="flex items-center space-x-3">
                            <Switch
                              checked={halfdayOptions.afternoon}
                              onChange={(e) =>
                                setHalfdayOptions((prev) => ({ ...prev, afternoon: e.target.checked }))
                              }
                              disabled={isLoading}
                            />
                            <div>
                              <div className="text-sm font-medium text-gray-900 dark:text-white">
                                🌇 Après-midi (14h - 20h)
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">6 heures de location</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="space-y-3">
                      {/* En-têtes des colonnes */}
                      <div className="grid grid-cols-7 gap-4 items-center px-4 py-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                        <div className="text-xs font-semibold text-gray-600 dark:text-gray-400">Jour</div>
                        <div className="text-xs font-semibold text-gray-600 dark:text-gray-400">Heure début</div>
                        <div className="text-xs font-semibold text-gray-600 dark:text-gray-400">Heure fin</div>
                        <div className="text-xs font-semibold text-gray-600 dark:text-gray-400">
                          {category.id === "hourly" ? "Prix/h (€)" : "Prix (€)"}
                        </div>
                        <div className="text-xs font-semibold text-gray-600 dark:text-gray-400">Capacité</div>
                        <div className="text-xs font-semibold text-gray-600 dark:text-gray-400">Activer</div>
                        <div className="text-xs font-semibold text-gray-600 dark:text-gray-400">Action</div>
                      </div>

                      {/* Jours - logique spéciale pour halfday */}
                      {category.id === "halfday" ? (
                        /* Pour halfday, on affiche les créneaux selon les toggles */
                        <div>
                          {/* Section jours de semaine */}
                          <div className="mb-6">
                            <h6 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center">
                              📅 Jours de semaine (Lundi - Vendredi)
                            </h6>
                            {DAYS_OF_WEEK.filter((day) => day.value !== "6" && day.value !== "0").map((day) => (
                              <div key={`${category.id}-${day.value}`}>
                                {/* Ligne matin si activée */}
                                {halfdayOptions.morning && (
                                  <div
                                    className="grid grid-cols-7 gap-4 items-center px-4 py-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors mb-2"
                                    data-halfday-type="morning"
                                    data-day={day.value}
                                  >
                                    <div className="font-medium text-gray-900 dark:text-white text-sm">
                                      {day.label} <span className="text-xs text-green-600">🌅 Matin</span>
                                    </div>
                                    <Select className="text-sm" defaultValue="08:00" data-field="start">
                                      <option value="08:00">08:00</option>
                                      <option value="09:00">09:00</option>
                                      <option value="10:00">10:00</option>
                                    </Select>
                                    <Select className="text-sm" defaultValue="14:00" data-field="end">
                                      <option value="13:00">13:00</option>
                                      <option value="14:00">14:00</option>
                                      <option value="15:00">15:00</option>
                                    </Select>
                                    <Input
                                      type="number"
                                      placeholder="Prix"
                                      className="text-sm"
                                      data-field="price"
                                      value={(() => {
                                        const currentPrices = getValues("prices") || [];
                                        const priceObj = currentPrices.find(
                                          (p) =>
                                            p.selectedDay === day.label &&
                                            p.type === "demi-journée" &&
                                            p.startHour === "08:00"
                                        );
                                        return priceObj && priceObj.price > 0 ? priceObj.price : "";
                                      })()}
                                      onChange={(e) => {
                                        const newPrice = Number(e.target.value);
                                        const currentPrices = getValues("prices") || [];
                                        const existingIndex = currentPrices.findIndex(
                                          (p) =>
                                            p.selectedDay === day.label &&
                                            p.type === "demi-journée" &&
                                            p.startHour === "08:00"
                                        );

                                        if (existingIndex >= 0) {
                                          currentPrices[existingIndex].price = newPrice;
                                        } else {
                                          currentPrices.push({
                                            price: newPrice,
                                            min: 1,
                                            max: 50,
                                            startHour: "08:00",
                                            endHour: "14:00",
                                            selectedDay: day.label,
                                            type: "demi-journée",
                                          });
                                        }
                                        setValue("prices", currentPrices);
                                        trigger("prices");
                                      }}
                                    />
                                    <Input
                                      type="number"
                                      placeholder="Capacité"
                                      className="text-sm"
                                      data-field="capacity"
                                      value={(() => {
                                        const currentPrices = getValues("prices") || [];
                                        const priceObj = currentPrices.find(
                                          (p) =>
                                            p.selectedDay === day.label &&
                                            p.type === "demi-journée" &&
                                            p.startHour === "08:00"
                                        );
                                        return priceObj?.max || "";
                                      })()}
                                      onChange={(e) => {
                                        const newCapacity = Number(e.target.value);
                                        const currentPrices = getValues("prices") || [];
                                        const existingIndex = currentPrices.findIndex(
                                          (p) =>
                                            p.selectedDay === day.label &&
                                            p.type === "demi-journée" &&
                                            p.startHour === "08:00"
                                        );

                                        if (existingIndex >= 0) {
                                          currentPrices[existingIndex].max = newCapacity;
                                        } else {
                                          currentPrices.push({
                                            price: 0,
                                            min: 1,
                                            max: newCapacity,
                                            startHour: "08:00",
                                            endHour: "14:00",
                                            selectedDay: day.label,
                                            type: "demi-journée",
                                          });
                                        }
                                        setValue("prices", currentPrices);
                                        trigger("prices");
                                      }}
                                    />
                                    <Switch
                                      data-field="enabled"
                                      checked={(() => {
                                        const currentPrices = getValues("prices") || [];
                                        const priceObj = currentPrices.find(
                                          (p) =>
                                            p.selectedDay === day.label &&
                                            p.type === "demi-journée" &&
                                            p.startHour === "08:00"
                                        );
                                        return !!priceObj;
                                      })()}
                                      onChange={(checked) => {
                                        const currentPrices = getValues("prices") || [];
                                        const existingIndex = currentPrices.findIndex(
                                          (p) =>
                                            p.selectedDay === day.label &&
                                            p.type === "demi-journée" &&
                                            p.startHour === "08:00"
                                        );

                                        if (checked && existingIndex < 0) {
                                          currentPrices.push({
                                            price: 0,
                                            min: 1,
                                            max: 50,
                                            startHour: "08:00",
                                            endHour: "14:00",
                                            selectedDay: day.label,
                                            type: "demi-journée",
                                          });
                                        } else if (!checked && existingIndex >= 0) {
                                          currentPrices.splice(existingIndex, 1);
                                        }
                                        setValue("prices", currentPrices);
                                        trigger("prices");
                                      }}
                                    />
                                    <button
                                      type="button"
                                      onClick={() => applyToAllMorning(day.value)}
                                      className="text-blue-600 hover:text-blue-800 text-sm"
                                    >
                                      Appliquer partout
                                    </button>
                                  </div>
                                )}
                                {/* Ligne après-midi si activée */}
                                {halfdayOptions.afternoon && (
                                  <div
                                    className="grid grid-cols-7 gap-4 items-center px-4 py-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors mb-2"
                                    data-halfday-type="afternoon"
                                    data-day={day.value}
                                  >
                                    <div className="font-medium text-gray-900 dark:text-white text-sm">
                                      {day.label} <span className="text-xs text-orange-600">🌇 A-midi</span>
                                    </div>
                                    <Select className="text-sm" defaultValue="14:00" data-field="start">
                                      <option value="13:00">13:00</option>
                                      <option value="14:00">14:00</option>
                                      <option value="15:00">15:00</option>
                                    </Select>
                                    <Select className="text-sm" defaultValue="20:00" data-field="end">
                                      <option value="19:00">19:00</option>
                                      <option value="20:00">20:00</option>
                                      <option value="21:00">21:00</option>
                                    </Select>
                                    <Input
                                      type="number"
                                      placeholder="Prix"
                                      className="text-sm"
                                      data-field="price"
                                      value={(() => {
                                        const currentPrices = getValues("prices") || [];
                                        const priceObj = currentPrices.find(
                                          (p) =>
                                            p.selectedDay === day.label &&
                                            p.type === "demi-journée" &&
                                            p.startHour === "14:00"
                                        );
                                        return priceObj && priceObj.price > 0 ? priceObj.price : "";
                                      })()}
                                      onChange={(e) => {
                                        const newPrice = Number(e.target.value);
                                        const currentPrices = getValues("prices") || [];
                                        const existingIndex = currentPrices.findIndex(
                                          (p) =>
                                            p.selectedDay === day.label &&
                                            p.type === "demi-journée" &&
                                            p.startHour === "14:00"
                                        );

                                        if (existingIndex >= 0) {
                                          currentPrices[existingIndex].price = newPrice;
                                        } else {
                                          currentPrices.push({
                                            price: newPrice,
                                            min: 1,
                                            max: 50,
                                            startHour: "14:00",
                                            endHour: "20:00",
                                            selectedDay: day.label,
                                            type: "demi-journée",
                                          });
                                        }
                                        setValue("prices", currentPrices);
                                        trigger("prices");
                                      }}
                                    />
                                    <Input
                                      type="number"
                                      placeholder="Capacité"
                                      className="text-sm"
                                      data-field="capacity"
                                      value={(() => {
                                        const currentPrices = getValues("prices") || [];
                                        const priceObj = currentPrices.find(
                                          (p) =>
                                            p.selectedDay === day.label &&
                                            p.type === "demi-journée" &&
                                            p.startHour === "14:00"
                                        );
                                        return priceObj?.max || "";
                                      })()}
                                      onChange={(e) => {
                                        const newCapacity = Number(e.target.value);
                                        const currentPrices = getValues("prices") || [];
                                        const existingIndex = currentPrices.findIndex(
                                          (p) =>
                                            p.selectedDay === day.label &&
                                            p.type === "demi-journée" &&
                                            p.startHour === "14:00"
                                        );

                                        if (existingIndex >= 0) {
                                          currentPrices[existingIndex].max = newCapacity;
                                        } else {
                                          currentPrices.push({
                                            price: 0,
                                            min: 1,
                                            max: newCapacity,
                                            startHour: "14:00",
                                            endHour: "20:00",
                                            selectedDay: day.label,
                                            type: "demi-journée",
                                          });
                                        }
                                        setValue("prices", currentPrices);
                                        trigger("prices");
                                      }}
                                    />
                                    <Switch
                                      data-field="enabled"
                                      checked={(() => {
                                        const currentPrices = getValues("prices") || [];
                                        const priceObj = currentPrices.find(
                                          (p) =>
                                            p.selectedDay === day.label &&
                                            p.type === "demi-journée" &&
                                            p.startHour === "14:00"
                                        );
                                        return !!priceObj;
                                      })()}
                                      onChange={(checked) => {
                                        const currentPrices = getValues("prices") || [];
                                        const existingIndex = currentPrices.findIndex(
                                          (p) =>
                                            p.selectedDay === day.label &&
                                            p.type === "demi-journée" &&
                                            p.startHour === "14:00"
                                        );

                                        if (checked && existingIndex < 0) {
                                          currentPrices.push({
                                            price: 0,
                                            min: 1,
                                            max: 50,
                                            startHour: "14:00",
                                            endHour: "20:00",
                                            selectedDay: day.label,
                                            type: "demi-journée",
                                          });
                                        } else if (!checked && existingIndex >= 0) {
                                          currentPrices.splice(existingIndex, 1);
                                        }
                                        setValue("prices", currentPrices);
                                        trigger("prices");
                                      }}
                                    />
                                    <button
                                      type="button"
                                      onClick={() => applyToAllAfternoon(day.value)}
                                      className="text-blue-600 hover:text-blue-800 text-sm"
                                    >
                                      Appliquer partout
                                    </button>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>

                          {/* Section Weekend */}
                          <div className="mb-6">
                            <h6 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center">
                              🏖️ Weekend (Samedi - Dimanche)
                            </h6>
                            {DAYS_OF_WEEK.filter((day) => day.value === "6" || day.value === "0").map((day) => (
                              <div key={`${category.id}-weekend-${day.value}`}>
                                {/* Ligne matin si activée */}
                                {halfdayOptions.morning && (
                                  <div
                                    className="grid grid-cols-7 gap-4 items-center px-4 py-3 bg-blue-50 dark:bg-blue-900/30 rounded-lg border border-blue-200 dark:border-blue-700 hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors mb-2"
                                    data-halfday-type="morning"
                                    data-day={day.value}
                                  >
                                    <div className="font-medium text-gray-900 dark:text-white text-sm">
                                      {day.label} <span className="text-xs text-green-600">🌅 Matin</span>
                                    </div>
                                    <Select className="text-sm" defaultValue="08:00" data-field="start">
                                      <option value="08:00">08:00</option>
                                      <option value="09:00">09:00</option>
                                      <option value="10:00">10:00</option>
                                    </Select>
                                    <Select className="text-sm" defaultValue="14:00" data-field="end">
                                      <option value="13:00">13:00</option>
                                      <option value="14:00">14:00</option>
                                      <option value="15:00">15:00</option>
                                    </Select>
                                    <Input
                                      type="number"
                                      placeholder="Prix"
                                      className="text-sm"
                                      data-field="price"
                                      value={(() => {
                                        const currentPrices = getValues("prices") || [];
                                        const priceObj = currentPrices.find(
                                          (p) =>
                                            p.selectedDay === day.label &&
                                            p.type === "demi-journée" &&
                                            p.startHour === "08:00"
                                        );
                                        return priceObj && priceObj.price > 0 ? priceObj.price : "";
                                      })()}
                                      onChange={(e) => {
                                        const newPrice = Number(e.target.value);
                                        const currentPrices = getValues("prices") || [];
                                        const existingIndex = currentPrices.findIndex(
                                          (p) =>
                                            p.selectedDay === day.label &&
                                            p.type === "demi-journée" &&
                                            p.startHour === "08:00"
                                        );

                                        if (existingIndex >= 0) {
                                          currentPrices[existingIndex].price = newPrice;
                                        } else {
                                          currentPrices.push({
                                            price: newPrice,
                                            min: 1,
                                            max: 50,
                                            startHour: "08:00",
                                            endHour: "14:00",
                                            selectedDay: day.label,
                                            type: "demi-journée",
                                          });
                                        }
                                        setValue("prices", currentPrices);
                                        trigger("prices");
                                      }}
                                    />
                                    <Input
                                      type="number"
                                      placeholder="Capacité"
                                      className="text-sm"
                                      data-field="capacity"
                                      value={(() => {
                                        const currentPrices = getValues("prices") || [];
                                        const priceObj = currentPrices.find(
                                          (p) =>
                                            p.selectedDay === day.label &&
                                            p.type === "demi-journée" &&
                                            p.startHour === "08:00"
                                        );
                                        return priceObj?.max || "";
                                      })()}
                                      onChange={(e) => {
                                        const newCapacity = Number(e.target.value);
                                        const currentPrices = getValues("prices") || [];
                                        const existingIndex = currentPrices.findIndex(
                                          (p) =>
                                            p.selectedDay === day.label &&
                                            p.type === "demi-journée" &&
                                            p.startHour === "08:00"
                                        );

                                        if (existingIndex >= 0) {
                                          currentPrices[existingIndex].max = newCapacity;
                                        } else {
                                          currentPrices.push({
                                            price: 0,
                                            min: 1,
                                            max: newCapacity,
                                            startHour: "08:00",
                                            endHour: "14:00",
                                            selectedDay: day.label,
                                            type: "demi-journée",
                                          });
                                        }
                                        setValue("prices", currentPrices);
                                        trigger("prices");
                                      }}
                                    />
                                    <Switch
                                      data-field="enabled"
                                      checked={(() => {
                                        const currentPrices = getValues("prices") || [];
                                        const priceObj = currentPrices.find(
                                          (p) =>
                                            p.selectedDay === day.label &&
                                            p.type === "demi-journée" &&
                                            p.startHour === "08:00"
                                        );
                                        return !!priceObj;
                                      })()}
                                      onChange={(checked) => {
                                        const currentPrices = getValues("prices") || [];
                                        const existingIndex = currentPrices.findIndex(
                                          (p) =>
                                            p.selectedDay === day.label &&
                                            p.type === "demi-journée" &&
                                            p.startHour === "08:00"
                                        );

                                        if (checked && existingIndex < 0) {
                                          currentPrices.push({
                                            price: 0,
                                            min: 1,
                                            max: 50,
                                            startHour: "08:00",
                                            endHour: "14:00",
                                            selectedDay: day.label,
                                            type: "demi-journée",
                                          });
                                        } else if (!checked && existingIndex >= 0) {
                                          currentPrices.splice(existingIndex, 1);
                                        }
                                        setValue("prices", currentPrices);
                                        trigger("prices");
                                      }}
                                    />
                                    <button
                                      type="button"
                                      onClick={() => applyToAllMorning(day.value)}
                                      className="text-blue-600 hover:text-blue-800 text-sm"
                                    >
                                      Appliquer partout
                                    </button>
                                  </div>
                                )}
                                {/* Ligne après-midi si activée */}
                                {halfdayOptions.afternoon && (
                                  <div
                                    className="grid grid-cols-7 gap-4 items-center px-4 py-3 bg-orange-50 dark:bg-orange-900/30 rounded-lg border border-orange-200 dark:border-orange-700 hover:bg-orange-100 dark:hover:bg-orange-900/50 transition-colors mb-2"
                                    data-halfday-type="afternoon"
                                    data-day={day.value}
                                  >
                                    <div className="font-medium text-gray-900 dark:text-white text-sm">
                                      {day.label} <span className="text-xs text-orange-600">🌇 A-midi</span>
                                    </div>
                                    <Select className="text-sm" defaultValue="14:00" data-field="start">
                                      <option value="13:00">13:00</option>
                                      <option value="14:00">14:00</option>
                                      <option value="15:00">15:00</option>
                                    </Select>
                                    <Select className="text-sm" defaultValue="20:00" data-field="end">
                                      <option value="19:00">19:00</option>
                                      <option value="20:00">20:00</option>
                                      <option value="21:00">21:00</option>
                                    </Select>
                                    <Input
                                      type="number"
                                      placeholder="Prix"
                                      className="text-sm"
                                      data-field="price"
                                      value={(() => {
                                        const currentPrices = getValues("prices") || [];
                                        const priceObj = currentPrices.find(
                                          (p) =>
                                            p.selectedDay === day.label &&
                                            p.type === "demi-journée" &&
                                            p.startHour === "14:00"
                                        );
                                        return priceObj && priceObj.price > 0 ? priceObj.price : "";
                                      })()}
                                      onChange={(e) => {
                                        const newPrice = Number(e.target.value);
                                        const currentPrices = getValues("prices") || [];
                                        const existingIndex = currentPrices.findIndex(
                                          (p) =>
                                            p.selectedDay === day.label &&
                                            p.type === "demi-journée" &&
                                            p.startHour === "14:00"
                                        );

                                        if (existingIndex >= 0) {
                                          currentPrices[existingIndex].price = newPrice;
                                        } else {
                                          currentPrices.push({
                                            price: newPrice,
                                            min: 1,
                                            max: 50,
                                            startHour: "14:00",
                                            endHour: "20:00",
                                            selectedDay: day.label,
                                            type: "demi-journée",
                                          });
                                        }
                                        setValue("prices", currentPrices);
                                        trigger("prices");
                                      }}
                                    />
                                    <Input
                                      type="number"
                                      placeholder="Capacité"
                                      className="text-sm"
                                      data-field="capacity"
                                      value={(() => {
                                        const currentPrices = getValues("prices") || [];
                                        const priceObj = currentPrices.find(
                                          (p) =>
                                            p.selectedDay === day.label &&
                                            p.type === "demi-journée" &&
                                            p.startHour === "14:00"
                                        );
                                        return priceObj?.max || "";
                                      })()}
                                      onChange={(e) => {
                                        const newCapacity = Number(e.target.value);
                                        const currentPrices = getValues("prices") || [];
                                        const existingIndex = currentPrices.findIndex(
                                          (p) =>
                                            p.selectedDay === day.label &&
                                            p.type === "demi-journée" &&
                                            p.startHour === "14:00"
                                        );

                                        if (existingIndex >= 0) {
                                          currentPrices[existingIndex].max = newCapacity;
                                        } else {
                                          currentPrices.push({
                                            price: 0,
                                            min: 1,
                                            max: newCapacity,
                                            startHour: "14:00",
                                            endHour: "20:00",
                                            selectedDay: day.label,
                                            type: "demi-journée",
                                          });
                                        }
                                        setValue("prices", currentPrices);
                                        trigger("prices");
                                      }}
                                    />
                                    <Switch
                                      data-field="enabled"
                                      checked={(() => {
                                        const currentPrices = getValues("prices") || [];
                                        const priceObj = currentPrices.find(
                                          (p) =>
                                            p.selectedDay === day.label &&
                                            p.type === "demi-journée" &&
                                            p.startHour === "14:00"
                                        );
                                        return !!priceObj;
                                      })()}
                                      onChange={(checked) => {
                                        const currentPrices = getValues("prices") || [];
                                        const existingIndex = currentPrices.findIndex(
                                          (p) =>
                                            p.selectedDay === day.label &&
                                            p.type === "demi-journée" &&
                                            p.startHour === "14:00"
                                        );

                                        if (checked && existingIndex < 0) {
                                          currentPrices.push({
                                            price: 0,
                                            min: 1,
                                            max: 50,
                                            startHour: "14:00",
                                            endHour: "20:00",
                                            selectedDay: day.label,
                                            type: "demi-journée",
                                          });
                                        } else if (!checked && existingIndex >= 0) {
                                          currentPrices.splice(existingIndex, 1);
                                        }
                                        setValue("prices", currentPrices);
                                        trigger("prices");
                                      }}
                                    />
                                    <button
                                      type="button"
                                      onClick={() => applyToAllAfternoon(day.value)}
                                      className="text-blue-600 hover:text-blue-800 text-sm"
                                    >
                                      Appliquer partout
                                    </button>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        /* Pour les autres catégories, logique avec séparation weekend */
                        <div>
                          {/* Section jours de semaine */}
                          <div className="mb-6">
                            <h6 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center">
                              📅 Jours de semaine (Lundi - Vendredi)
                            </h6>
                            {DAYS_OF_WEEK.filter((day) => day.value !== "6" && day.value !== "0").map((day) => (
                              <div
                                key={`${category.id}-${day.value}`}
                                className="grid grid-cols-7 gap-4 items-center px-4 py-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors mb-2"
                              >
                                <div className="font-medium text-gray-900 dark:text-white text-sm">
                                  {day.label}
                                </div>

                                <Select className="text-sm">
                                  <option value="08:00">08:00</option>
                                  <option value="09:00">09:00</option>
                                  <option value="10:00">10:00</option>
                                  <option value="11:00">11:00</option>
                                  <option value="12:00">12:00</option>
                                  <option value="13:00">13:00</option>
                                  <option value="14:00">14:00</option>
                                  <option value="15:00">15:00</option>
                                  <option value="16:00">16:00</option>
                                  <option value="17:00">17:00</option>
                                  <option value="18:00">18:00</option>
                                  <option value="19:00">19:00</option>
                                  <option value="20:00">20:00</option>
                                </Select>

                                <Select className="text-sm">
                                  <option value="16:00">16:00</option>
                                  <option value="17:00">17:00</option>
                                  <option value="18:00">18:00</option>
                                  <option value="19:00">19:00</option>
                                  <option value="20:00">20:00</option>
                                  <option value="21:00">21:00</option>
                                  <option value="22:00">22:00</option>
                                  <option value="23:00">23:00</option>
                                  <option value="00:00">00:00</option>
                                </Select>

                                <Input
                                  type="number"
                                  placeholder="25"
                                  className="text-sm"
                                  key={`${category.id}-${day.label}-price-${refreshKey}`}
                                  defaultValue={(() => {
                                    const currentPrices = getValues("prices") || [];
                                    const typeMap = {
                                      hourly: "heure",
                                      evening: "soirée",
                                      fullday: "journée complète",
                                    } as const;
                                    const targetType = typeMap[category.id as keyof typeof typeMap] as
                                      | "heure"
                                      | "soirée"
                                      | "journée complète";
                                    const priceObj = currentPrices.find(
                                      (p) => p.selectedDay === day.label && p.type === targetType
                                    );
                                    return priceObj && priceObj.price > 0 ? priceObj.price : "";
                                  })()}
                                  onChange={(e) => {
                                    const inputValue = e.target.value;
                                    const currentPrices = getValues("prices") || [];
                                    const typeMap = {
                                      hourly: "heure",
                                      evening: "soirée",
                                      fullday: "journée complète",
                                    } as const;
                                    const targetType = typeMap[category.id as keyof typeof typeMap] as
                                      | "heure"
                                      | "soirée"
                                      | "journée complète";

                                    // Si le champ est vide, on supprime l'entrée
                                    if (inputValue === "" || inputValue === "0") {
                                      const updatedPrices = currentPrices.filter(
                                        (p) => !(p.selectedDay === day.label && p.type === targetType)
                                      );
                                      setValue("prices", updatedPrices);
                                      return;
                                    }

                                    const updatedPrices = [...currentPrices];
                                    const existingIndex = updatedPrices.findIndex(
                                      (p) => p.selectedDay === day.label && p.type === targetType
                                    );

                                    // Définir les heures par défaut selon le type
                                    let defaultStartHour = "08:00";
                                    let defaultEndHour = "20:00";

                                    if (targetType === "soirée") {
                                      defaultStartHour = "18:00";
                                      defaultEndHour = "02:00";
                                    } else if (targetType === "journée complète") {
                                      defaultStartHour = "08:00";
                                      defaultEndHour = "20:00";
                                    }

                                    const newPrice = {
                                      selectedDay: day.label,
                                      type: targetType,
                                      price: parseFloat(inputValue),
                                      min: 1,
                                      max: (data as any)?.visitor || 50,
                                      startHour: defaultStartHour,
                                      endHour: defaultEndHour,
                                    };

                                    if (existingIndex >= 0) {
                                      updatedPrices[existingIndex] = {
                                        ...updatedPrices[existingIndex],
                                        ...newPrice,
                                      };
                                    } else {
                                      updatedPrices.push(newPrice);
                                    }

                                    setValue("prices", updatedPrices);
                                    trigger("prices"); // Force la revalidation
                                  }}
                                />

                                <Input
                                  type="number"
                                  placeholder={data?.visitor?.toString() || "50"}
                                  defaultValue={data?.visitor}
                                  className="text-sm"
                                />

                                <div className="flex justify-center">
                                  <Switch size="small" />
                                </div>

                                <div className="flex justify-center">
                                  <button
                                    type="button"
                                    onClick={() => applyToAllDays(day.label, category.id)}
                                    className="px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors font-medium"
                                    title={`Appliquer la configuration de ${day.label} à tous les jours`}
                                  >
                                    Appliquer partout
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>

                          {/* Section Weekend */}
                          <div className="mb-6">
                            <h6 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center">
                              🏖️ Weekend (Samedi - Dimanche)
                            </h6>
                            {DAYS_OF_WEEK.filter((day) => day.value === "6" || day.value === "0").map((day) => (
                              <div
                                key={`${category.id}-weekend-${day.value}`}
                                className="grid grid-cols-7 gap-4 items-center px-4 py-3 bg-blue-50 dark:bg-blue-900/30 rounded-lg border border-blue-200 dark:border-blue-700 hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors mb-2"
                              >
                                <div className="font-medium text-blue-800 dark:text-blue-200 text-sm">
                                  {day.label}{" "}
                                  <span className="text-xs text-blue-600 dark:text-blue-400">🏖️ Weekend</span>
                                </div>

                                <Select className="text-sm">
                                  <option value="08:00">08:00</option>
                                  <option value="09:00">09:00</option>
                                  <option value="10:00">10:00</option>
                                  <option value="11:00">11:00</option>
                                  <option value="12:00">12:00</option>
                                  <option value="13:00">13:00</option>
                                  <option value="14:00">14:00</option>
                                  <option value="15:00">15:00</option>
                                  <option value="16:00">16:00</option>
                                  <option value="17:00">17:00</option>
                                  <option value="18:00">18:00</option>
                                  <option value="19:00">19:00</option>
                                  <option value="20:00">20:00</option>
                                </Select>

                                <Select className="text-sm">
                                  <option value="16:00">16:00</option>
                                  <option value="17:00">17:00</option>
                                  <option value="18:00">18:00</option>
                                  <option value="19:00">19:00</option>
                                  <option value="20:00">20:00</option>
                                  <option value="21:00">21:00</option>
                                  <option value="22:00">22:00</option>
                                  <option value="23:00">23:00</option>
                                  <option value="00:00">00:00</option>
                                </Select>

                                <Input
                                  type="number"
                                  placeholder="25"
                                  className="text-sm"
                                  key={`${category.id}-weekend-${day.label}-price-${refreshKey}`}
                                  defaultValue={(() => {
                                    const currentPrices = getValues("prices") || [];
                                    const typeMap = {
                                      hourly: "heure",
                                      evening: "soirée",
                                      fullday: "journée complète",
                                    } as const;
                                    const targetType = typeMap[category.id as keyof typeof typeMap] as
                                      | "heure"
                                      | "soirée"
                                      | "journée complète";
                                    const priceObj = currentPrices.find(
                                      (p) => p.selectedDay === day.label && p.type === targetType
                                    );
                                    return priceObj && priceObj.price > 0 ? priceObj.price : "";
                                  })()}
                                  onChange={(e) => {
                                    const inputValue = e.target.value;
                                    const currentPrices = getValues("prices") || [];
                                    const typeMap = {
                                      hourly: "heure",
                                      evening: "soirée",
                                      fullday: "journée complète",
                                    } as const;
                                    const targetType = typeMap[category.id as keyof typeof typeMap] as
                                      | "heure"
                                      | "soirée"
                                      | "journée complète";

                                    // Si le champ est vide, on supprime l'entrée
                                    if (inputValue === "" || inputValue === "0") {
                                      const updatedPrices = currentPrices.filter(
                                        (p) => !(p.selectedDay === day.label && p.type === targetType)
                                      );
                                      setValue("prices", updatedPrices);
                                      return;
                                    }

                                    const updatedPrices = [...currentPrices];
                                    const existingIndex = updatedPrices.findIndex(
                                      (p) => p.selectedDay === day.label && p.type === targetType
                                    );

                                    // Définir les heures par défaut selon le type
                                    let defaultStartHour = "08:00";
                                    let defaultEndHour = "20:00";

                                    if (targetType === "soirée") {
                                      defaultStartHour = "18:00";
                                      defaultEndHour = "02:00";
                                    } else if (targetType === "journée complète") {
                                      defaultStartHour = "08:00";
                                      defaultEndHour = "20:00";
                                    }

                                    const newPrice = {
                                      selectedDay: day.label,
                                      type: targetType,
                                      price: parseFloat(inputValue),
                                      min: 1,
                                      max: (data as any)?.visitor || 50,
                                      startHour: defaultStartHour,
                                      endHour: defaultEndHour,
                                    };

                                    if (existingIndex >= 0) {
                                      updatedPrices[existingIndex] = {
                                        ...updatedPrices[existingIndex],
                                        ...newPrice,
                                      };
                                    } else {
                                      updatedPrices.push(newPrice);
                                    }

                                    setValue("prices", updatedPrices);
                                    trigger("prices"); // Force la revalidation
                                  }}
                                />

                                <Input
                                  type="number"
                                  placeholder={data?.visitor?.toString() || "50"}
                                  defaultValue={data?.visitor}
                                  className="text-sm"
                                />

                                <div className="flex justify-center">
                                  <Switch size="small" />
                                </div>

                                <div className="flex justify-center">
                                  <button
                                    type="button"
                                    onClick={() => applyToAllDays(day.label, category.id)}
                                    className="px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors font-medium"
                                    title={`Appliquer la configuration de ${day.label} à tous les jours`}
                                  >
                                    Appliquer partout
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}

              {errors.prices?.message && (
                <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <div className="text-sm text-red-600 dark:text-red-400">{errors.prices.message}</div>
                </div>
              )}

              {/* Indicateur des prix configurés */}
              <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                <div className="text-sm text-blue-800 dark:text-blue-200">
                  <strong>Prix configurés :</strong> {getValues("prices")?.length || 0} tarifs
                  {pricingCategories.filter((c) => c.enabled).length > 0 && (
                    <>
                      <br />
                      <strong>Catégories actives :</strong>{" "}
                      {pricingCategories
                        .filter((c) => c.enabled)
                        .map((c) => c.title)
                        .join(", ")}
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-between w-full space-x-5">
              <FormItem label="Frais de ménage (€)" className="mt-3">
                <div className="relative">
                  <div className="absolute inset-y-0 z-10 flex items-center pl-1 pointer-events-none right-5">
                    <span className="text-gray-500">€</span>
                  </div>
                  <Input
                    className={`mt-1.5 ${errors.cleaning_costs?.message ? "border-red-500" : ""}`}
                    placeholder="Frais de ménage"
                    {...register("cleaning_costs", { setValueAs: (v) => (v === "" ? undefined : parseInt(v)) })}
                    type="text"
                  />
                </div>
                <ErrorMessage message={errors.cleaning_costs?.message || ""} />
              </FormItem>

              {getValues("cleaning_costs") !== 0 && getValues("cleaning_costs") !== undefined && (
                <FormItem label="Sélectionner liste des événements" className="mt-3">
                  <Multiselect
                    options={eventsList?.map((item: any) => ({ label: item, value: item }))}
                    selectedValues={getValues("cleaning_costs_events")?.map((label: string) => ({
                      label,
                      value: label,
                    }))}
                    onSelect={(selectedList) => {
                      const labels = selectedList.map((item: { label: any }) => item.label);
                      setValue("cleaning_costs_events", labels);
                    }}
                    onRemove={(selectedList) => {
                      const labels = selectedList.map((item: { label: any }) => item.label);
                      setValue("cleaning_costs_events", labels);
                    }}
                    displayValue="label"
                    className={errors.cleaning_costs_events?.message ? "border-red-500" : ""}
                    placeholder="Sélectionner événements"
                    closeOnSelect={false}
                    disable={isLoading}
                    showCheckbox
                    isObject={true}
                    style={{
                      chips: { borderRadius: "20px" },
                      multiselectContainer: { borderRadius: "10px" },
                      searchBox: { borderRadius: "10px" },
                      optionContainer: { borderRadius: "10px" },
                    }}
                  />
                  <ErrorMessage message={errors.cleaning_costs_events?.message || ""} />
                </FormItem>
              )}
            </div>

            <FormItem label="Montant du dépôt de garantie (€) *" className="mt-3">
              <div className="md:w-1/2">
                <div className="relative">
                  <div className="absolute inset-y-0 z-10 flex items-center pl-1 pointer-events-none right-5">
                    <span className="text-gray-500">€</span>
                  </div>
                  <Input
                    className={`mt-1.5 ${errors.guarantee?.message ? "border-red-500" : ""}`}
                    placeholder="0"
                    {...register("guarantee", { valueAsNumber: true })}
                    type="number"
                    defaultValue={0}
                  />
                </div>
              </div>
              <ErrorMessage message={errors.guarantee?.message || ""} />
            </FormItem>
          </div>
        </div>
      </form>
    </Fragment>
  );
};

export default LocationAvailibility;
